import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useCart } from '../../../context/CartContext';
import { FaShoppingCart, FaPlus, FaMinus, FaHeart, FaStar } from 'react-icons/fa';

export function MedicineCard({ item }) {
  const navigate = useNavigate();
  const { cart, addMedicine, updateQuantity, removeItem, isAuthenticated } = useCart();
  const [imageError, setImageError] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastKnownQty, setLastKnownQty] = useState(0); // Track last known quantity

  // Get quantity and cart item ID from backend cart - recalculate on every render
  const getCartInfo = () => {
    if (!cart || !cart.medicine_items) {
      return { quantity: 0, cartItemId: null };
    }
    const cartItem = cart.medicine_items.find(cartItem => cartItem.medicine?.id === item.id);
    return cartItem ? { quantity: cartItem.quantity, cartItemId: cartItem.id } : { quantity: 0, cartItemId: null };
  };

  const { quantity: backendQty, cartItemId } = getCartInfo();

  // Always use backend quantity for display
  const qty = backendQty;

  // But for the condition to show +/- buttons, also check if we're updating and had items before
  const hasItems = qty > 0 || (isUpdating && lastKnownQty > 0);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    setIsAddingToCart(true);
    try {
      await addMedicine(item.id, 1);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(error.message || 'Failed to add to cart');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleUpdateQuantity = async (e, newQuantity) => {
    e.preventDefault();
    e.stopPropagation();

    if (!cartItemId) return;
    if (isUpdating) return; // Prevent double-clicks

    // Save current quantity before updating
    setLastKnownQty(backendQty);
    setIsUpdating(true);

    try {
      // If quantity reaches 0, remove the item
      if (newQuantity < 1) {
        await removeItem('medicine', cartItemId);
      } else {
        await updateQuantity('medicine', cartItemId, newQuantity);
      }
    } catch (error) {
      console.error('Error updating/removing item:', error);
      alert(error.message || 'Failed to update cart');
    } finally {
      // Re-enable button after update completes
      setTimeout(() => {
        setIsUpdating(false);
      }, 500);
    }
  };

  const discount = Math.round(
    ((item.mrp - item.offer_price) / item.mrp) * 100
  );

  // Get initials from medicine name
  const getInitials = (name) => {
    if (!name) return 'M';
    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // Medicine icon component for fallback - tablet style with medical elements
  const MedicineIcon = () => {
    const initials = getInitials(item.name);
    const primaryColor = '#10495e';
    const lightColor = '#e0f2f1';

    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 p-2 relative">
        {/* Medical Cross - Top Left */}
        <div className="absolute top-2 left-2 opacity-20">
          <div className="relative w-4 h-4">
            <div className="absolute top-1/2 left-0 w-full h-1 transform -translate-y-1/2" style={{ backgroundColor: primaryColor }} />
            <div className="absolute top-0 left-1/2 w-1 h-full transform -translate-x-1/2" style={{ backgroundColor: primaryColor }} />
          </div>
        </div>

        {/* Droplet - Top Right */}
        <div className="absolute top-1 right-2 opacity-20">
          <svg width="12" height="16" viewBox="0 0 12 16" fill={primaryColor}>
            <path d="M6 0C6 0 0 6 0 10C0 13.3137 2.68629 16 6 16C9.31371 16 12 13.3137 12 10C12 6 6 0 6 0Z" />
          </svg>
        </div>

        {/* Main Tablet */}
        <div className="relative">
          {/* Tablet Shape - Rectangle with rounded corners */}
          <div
            className="rounded-lg px-5 py-4 md:px-7 md:py-5 shadow-xl flex items-center justify-center border-3 relative overflow-hidden"
            style={{
              background: `linear-gradient(145deg, ${primaryColor} 0%, #0d3a4d 100%)`,
              borderColor: primaryColor,
              borderWidth: '3px',
              borderStyle: 'solid',
            }}
          >
            {/* Dividing line in middle (like tablet score line) */}
            <div
              className="absolute top-0 left-1/2 w-0.5 h-full transform -translate-x-1/2 opacity-40"
              style={{ backgroundColor: '#fff' }}
            />

            {/* Shine effect */}
            <div
              className="absolute top-0 left-0 w-1/3 h-full opacity-20"
              style={{
                background: 'linear-gradient(120deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
              }}
            />

            {/* Initials */}
            <span className="text-white text-base md:text-lg font-bold tracking-widest relative z-10">
              {initials}
            </span>
          </div>

          {/* Small round pill - bottom left */}
          <div
            className="absolute -bottom-1 -left-2 w-3 h-3 rounded-full shadow-md border"
            style={{
              backgroundColor: lightColor,
              borderColor: primaryColor,
              borderWidth: '1.5px'
            }}
          />

          {/* Small capsule - top right */}
          <div
            className="absolute -top-1 -right-2 rounded-full shadow-md border flex items-center justify-center"
            style={{
              backgroundColor: primaryColor,
              borderColor: primaryColor,
              borderWidth: '1.5px',
              width: '14px',
              height: '8px'
            }}
          />

          {/* Small round pill - middle right */}
          <div
            className="absolute top-1/2 -right-1 w-2.5 h-2.5 rounded-full shadow-sm border transform -translate-y-1/2"
            style={{
              backgroundColor: lightColor,
              borderColor: primaryColor,
              borderWidth: '1px'
            }}
          />
        </div>

        {/* Syringe Icon - Bottom Right */}
        <div className="absolute bottom-2 right-2 opacity-15">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2">
            <path d="M18 2l4 4M7.5 12.5l-5 5a2 2 0 0 0 0 2.828l.707.707a2 2 0 0 0 2.828 0l5-5M12 7l5 5M9 4l1.414 1.414M4 9l1.414 1.414" />
          </svg>
        </div>

        {/* Pills scattered - Bottom Left */}
        <div className="absolute bottom-1.5 left-2 opacity-20 flex gap-0.5">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor }} />
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor }} />
        </div>
      </div>
    );
  };

  const shouldShowFallback = () => {
    return !item.image || imageError;
  };

  return (
    <div className="w-full max-w-xs bg-white rounded-xl p-3 mx-auto shadow-lg border border-gray-200 relative transition-all duration-300 cursor-pointer hover:shadow-xl hover:scale-105 overflow-hidden">
      {/* Discount Badge */}
      {discount > 0 && (
        <div className="absolute top-3 left-3 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl px-2.5 py-1 z-10 shadow-md">
          <span className="text-white text-xs font-bold tracking-wide">{discount}% OFF</span>
        </div>
      )}

      {/* Wishlist Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsWishlisted(!isWishlisted);
        }}
        className="absolute top-3 right-3 bg-white border-none rounded-full w-8 h-8 md:w-9 md:h-9 flex items-center justify-center cursor-pointer shadow-md transition-all duration-200 z-10 hover:scale-110"
        style={{ color: isWishlisted ? '#ff4757' : '#a4b0be' }}
      >
        <FaHeart className="text-sm" />
      </button>

      {/* Link wrapper for image and title */}
      <Link
        to={`/medicine/${item.id}`}
        className="text-inherit no-underline block"
      >
        {/* Image Container */}
        <div className="w-full h-24 md:h-28 mb-2 rounded-lg overflow-hidden bg-gray-100">
          {shouldShowFallback() ? (
            <MedicineIcon />
          ) : (
            <img
              src={item.image}
              alt={item.name}
              className="w-full h-full object-contain transition-transform duration-300 hover:scale-105"
              onError={() => setImageError(true)}
              onLoad={() => setImageError(false)}
            />
          )}
        </div>

        {/* Product Info */}
        <div className="mb-2">
          <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-1.5 leading-tight h-10 overflow-hidden line-clamp-2">
            {item.name}
          </h3>

          {/* Rating */}
          <div className="flex items-center gap-1">
            <FaStar className="text-yellow-500 text-xs" />
            <span className="text-xs md:text-sm text-gray-600 font-medium">4.5</span>
          </div>
        </div>
      </Link>

      {/* Price Section */}
      <div className="mb-2.5 py-1.5 border-t border-b border-gray-200">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-base md:text-lg font-bold text-green-700">₹{item.offer_price}</span>
          {item.mrp > item.offer_price && (
            <span className="text-sm text-gray-500 line-through">₹{item.mrp}</span>
          )}
        </div>
        {Math.round((parseFloat(item.mrp) - parseFloat(item.offer_price)) * 100) >= 100 && (
          <div className="text-xs text-green-600 font-medium">You save ₹{(parseFloat(item.mrp) - parseFloat(item.offer_price)).toFixed(2).replace(/\.00$/, '')}</div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mb-2">
        {hasItems ? (
          <div className="flex flex-col gap-2">
            {/* Quantity Controls */}
            <div className="flex items-center justify-center gap-3 py-2 px-3 bg-gray-50 rounded-lg border border-gray-200">
              <button
                onClick={(e) => handleUpdateQuantity(e, qty - 1)}
                disabled={isUpdating}
                className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded-lg text-gray-700 font-bold text-lg transition-all duration-200 hover:bg-gray-100 hover:scale-110 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
              >
                <FaMinus className="text-xs" />
              </button>
              <span className="text-base md:text-lg font-bold text-gray-800 min-w-[30px] text-center">
                {qty}
              </span>
              <button
                onClick={(e) => handleUpdateQuantity(e, qty + 1)}
                disabled={isUpdating}
                className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded-lg text-gray-700 font-bold text-lg transition-all duration-200 hover:bg-gray-100 hover:scale-110 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
              >
                <FaPlus className="text-xs" />
              </button>
            </div>
            {/* Go to Cart Button */}
            <Link
              to="/cart"
              onClick={(e) => e.stopPropagation()}
              className="w-full bg-green-600 text-white border-none rounded-lg py-2.5 px-3 text-sm md:text-base font-semibold cursor-pointer flex items-center justify-center gap-2 transition-all duration-300 hover:bg-green-700 hover:scale-105 shadow-md no-underline"
            >
              <FaShoppingCart className="text-xs md:text-sm" />
              <span>Go to Cart</span>
            </Link>
          </div>
        ) : (
          <button
            onClick={handleAddToCart}
            disabled={isAddingToCart}
            className="w-full text-white border-none rounded-lg py-2.5 md:py-3 px-3 text-sm md:text-base font-semibold cursor-pointer flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-90 hover:scale-105 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#104a5f', boxShadow: '0 2px 8px rgba(16, 74, 95, 0.2)' }}
          >
            {isAddingToCart ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Adding...</span>
              </>
            ) : (
              <>
                <FaShoppingCart className="text-xs md:text-sm" />
                <span>Add to Cart</span>
              </>
            )}
          </button>
        )}
      </div>

    </div>
  );
}